/* eslint-disable react/prop-types */
/*
Author: Chaitanya
*/

import React, { useState, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb } from '@global/screens';
import Toast from 'react-native-toast-message';

import {
  setTitleHeader,
  setDefaultStackOption,
  hasNoAccess,
  exportPdf,
} from '@global/helpers';

import HamburgerButton from '@components/navbar/HamburgerButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import BackButton from '@components/navbar/BackButton';
import MoreButton from '@components/navbar/MoreButton';
import CommentHeaderButton from '@components/comment/CommentHeaderButton';
import ModalCancel from '@components/modal/ModalCancel';

import SkpExternalScreen from '@/screens/skp/skp-external/SkpExternalScreen';
import SkpExternalDetailScreen from '@/screens/skp/skp-external/SkpExternalDetailScreen';
import SkpExternalFormScreen from '@/screens/skp/skp-external/SkpExternalFormScreen';
import SkpExternalBudgetTableScreen from '@/screens/skp/skp-external/SkpExternalBudgetTableScreen';
import SkpExternalListScreen from '@/screens/skp/skp-external/SkpExternalListScreen';

import { MsOrder } from '@api/ms-order';
import { showAlert } from '@/store/global';
import { showPopup } from '@/store/modal';
import { setSavedFilter } from '@global/helpers';

const Stack = createStackNavigator();

const EXPORT_SKP_EXTERNAL_ROLES = [
  'sales_admin',
  'salesman',
  'sales_supervisor',
  'branch_head',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'superadmin',
  'finance_supervisor',
  'finance_head',
  'tax_support',
  'distributor_manager',
  'billing_supervisor',
  'general_manager_marketing',
  'general_manager',
  'budget_controller',
  'marketing_admin',
  'marketing_support',
  'general',
  'regional_distributor_manager',
];

const DETAIL_ACCESS_ROLES = [
  'superadmin',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'sales_admin',
  'salesman',
  'marketing_admin',
  'general_manager_marketing',
  'finance_supervisor',
  'general',
];

const CANCEL_DELETE_SKP_EXTERNAL_ROLES = [
  'superadmin',
  'general',
  'regional_manager',
  'general_manager_marketing',
  'marketing_admin',
  'group_key_account_manager',
  'national_key_account_manager',
];

const getSkpExternalScreenOptions = (dispatch, insets) => (props) => {
  const exportSKPExternal = !hasNoAccess(EXPORT_SKP_EXTERNAL_ROLES);
  let isBack = false;
  let title = 'SKP External';

  if (props?.route?.params?.titleScreen) {
    isBack = true;
    title = `${title} ${
      !props?.route?.params?.isHome ? props.route.params.titleScreen : ''
    }`;
  }

  const showFilter = () => {
    dispatch(showPopup({ name: 'showFilter', value: 'skp-external' }));
  };

  const showExport = () => {
    dispatch(showPopup({ name: 'showSkpExternalExport', value: true }));
  };

  const goBack = () => {
    props.navigation.navigate('HomeNavigator');
  };

  const menus = [{ type: 'function', name: 'Filter', callback: showFilter }];

  if (exportSKPExternal) {
    menus.push({
      type: 'function',
      name: 'Export',
      callback: showExport,
    });
  }

  return {
    title: setTitleHeader(title),
    headerLeft: () =>
      isBack ? (
        <BackButton {...props} onPress={() => goBack()} />
      ) : (
        <HamburgerButton {...props} />
      ),
    headerRight: () =>
      isWeb ? (
        <NotificationSettingButton {...props} />
      ) : menus.length ? (
        <MoreButton menu={menus} {...props} />
      ) : (
        false
      ),
  };
};

const getSkpExternalFormScreenOptions = () => (props) => {
  const route = props.route;
  let title = 'Add SKP External';
  if (route.params && route.params.titleScreen) {
    title = 'Edit SKP External';
  }

  const goBack = () => {
    if (route.params && route.params.titleScreen) {
      props.navigation.navigate('SkpExternalDetailScreen', route.params);
    } else {
      // Add mode: back to the list it was launched for (menu passes `previous`),
      // not the generic home dashboard.
      props.navigation.navigate(route.params?.previous || 'SkpExternalScreen');
    }
  };

  return {
    title: setTitleHeader(title),
    headerLeft: () => (
      <BackButton {...props} notUseLastRoute={true} onPress={() => goBack()} />
    ),
    headerRight: isWeb ? () => <NotificationSettingButton {...props} /> : false,
  };
};

const getSkpExternalDetailScreenOptions =
  (
    dispatch,
    tempData,
    setCancelId,
    setCancelStatus,
    setShowCancelModal,
    setDeleteId,
    setShowDeleteModal,
    cancelNavigationRef,
    deleteNavigationRef,
  ) =>
  (props) => {
    const hasAccess = !hasNoAccess(DETAIL_ACCESS_ROLES);
    const canCancelOrDelete = !hasNoAccess(CANCEL_DELETE_SKP_EXTERNAL_ROLES);
    const route = props.route;
    let title = 'SKP External Detail';
    if (route?.params?.titleScreen) {
      title = route.params.titleScreen;
    }

    const onExportPdf = async () => {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      if (route.params.id) {
        try {
          await exportPdf('skp', route.params.id);
          dispatch(showPopup({ name: 'isLoading', value: false }));
        } catch (err) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
    };

    const handleCancelStatus = (id, status) => {
      dispatch(
        showAlert({
          show: true,
          title: 'Cancel SKP External',
          description:
            'This SKP External will be cancelled. Do you want to proceed?',
          onCallback: () => {
            setCancelId(id);
            setCancelStatus(status);
            setShowCancelModal(true);
            cancelNavigationRef.current = props.navigation;
          },
        }),
      );
    };

    const removeSkpExternal = () => {
      dispatch(
        showAlert({
          show: true,
          title: 'Delete SKP External',
          description:
            'This skp external will be deleted. Do you want to proceed?',
          onCallback: () => {
            setDeleteId(route.params.id);
            setShowDeleteModal(true);
            deleteNavigationRef.current = props.navigation;
          },
        }),
      );
    };

    const menus = [];
    if (
      hasAccess &&
      !['need_approval', 'approved', 'cancelled', 'completed'].includes(
        tempData,
      )
    ) {
      menus.push({
        type: 'redirect',
        name: 'Edit',
        screen: 'SkpExternalFormScreen',
        params: route.params,
      });
    }

    if (tempData != 'cancelled') {
      menus.push({
        type: 'function',
        name: 'Download PDF',
        callback: () => onExportPdf(),
      });
    }

    if (
      canCancelOrDelete &&
      ['draft', 'joined', 'rejected', 'approved', 'completed'].includes(
        tempData,
      )
    ) {
      menus.push({
        type: 'function',
        name: 'Cancel',
        callback: () => handleCancelStatus(route.params.id, 'cancelled'),
      });
    }

    if (canCancelOrDelete && (tempData == 'draft' || tempData == 'joined')) {
      menus.push({
        type: 'function',
        name: 'Delete',
        callback: () => removeSkpExternal(),
      });
    }

    const goBack = () => {
      if (route.params?.afterCancel) {
        setSavedFilter({ reset: true });
        props.navigation.reset({
          index: 0,
          routes: [{ name: 'SkpExternalScreen' }],
        });
        return;
      }
      const isFromPromotionAgreementExternalList =
        route?.params?.fromPromotionAgreementExternalList;

      if (isFromPromotionAgreementExternalList) {
        const parentNavigation =
          typeof props.navigation?.getParent === 'function'
            ? props.navigation.getParent()
            : null;

        if (parentNavigation) {
          parentNavigation.navigate('SkpInternalNavigator');
        } else {
          props.navigation?.navigate('SkpInternalNavigator');
        }
        return;
      }

      props.navigation.navigate('SkpExternalScreen');
    };

    return {
      title: setTitleHeader(title),
      // [B2B-5891 fix] React Navigation's default Header reserves a fixed 72px for a
      // left-aligned title's right-side neighbor, assuming a single button. This screen's
      // native headerRight can render TWO buttons (Comment + More) side by side (~50px
      // each), so the title's auto-computed maxWidth is too generous and its box overlaps
      // the second (Comment) button on long names — same root cause as SkpInternalDetailScreen.
      // Only applies once the Comment button is actually shown (route.params?.id present).
      headerTitleContainerStyle:
        !isWeb && route.params?.id ? { marginRight: 50 } : undefined,
      headerLeft: () => (
        <BackButton
          {...props}
          notUseLastRoute={true}
          onPress={() => goBack()}
        />
      ),
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <View style={styles.headerRightRow}>
            {route.params?.id ? (
              <CommentHeaderButton
                onPress={() =>
                  props.navigation.setParams({ showComments: true })
                }
                drawerVisible={Boolean(route.params?.showComments)}
                entityType="skp_external"
                entityId={route.params.id}
              />
            ) : (
              false
            )}
            {tempData === 'cancelled' ? null : (
              <MoreButton menu={menus} {...props} />
            )}
          </View>
        ),
    };
  };

const getSkpExternalBudgetTableScreenOptions = () => (props) => {
  const route = props.route;
  const title = 'Brand & Nominal Budget';
  const goBack = () => {
    props.navigation.navigate('SkpExternalDetailScreen', route.params);
  };

  return {
    title: setTitleHeader(title),
    headerLeft: () => <BackButton {...props} onPress={() => goBack()} />,
    headerRight: isWeb ? () => <NotificationSettingButton {...props} /> : false,
  };
};

const getSkpExternalListScreenOptions = () => (props) => {
  const route = props.route;
  const title = 'Promotion Agreement External';
  const goBack = () => {
    if (route.params?.isFromExternal) {
      props.navigation.navigate('SkpExternalDetailScreen', {
        id: route.params.skpExternalId,
      });
    } else {
      props.navigation.navigate('SkpExternalDetailScreen', route.params);
    }
  };

  return {
    title: setTitleHeader(title),
    headerLeft: () => <BackButton {...props} onPress={() => goBack()} />,
    headerRight: isWeb ? () => <NotificationSettingButton {...props} /> : false,
  };
};

const SkpExternalNavigator = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const tempData = useSelector((state) => state.global.tempData);
  const userData = useSelector((state) => state.auth?.user);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [cancelStatus, setCancelStatus] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const cancelNavigationRef = useRef(null);
  const deleteNavigationRef = useRef(null);

  const handleCancelSubmit = async (reason) => {
    try {
      if (cancelId && cancelStatus) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {
          reason_cancelled: reason,
        };
        const requestConfirm = await MsOrder.changeStatusSkpExternal(
          cancelId,
          cancelStatus,
          payload,
        );

        if (requestConfirm && requestConfirm.data && requestConfirm.data.data) {
          let title = '';
          let desc = '';
          if (cancelStatus == 'cancelled') {
            title = 'SKP External Cancelled';
            desc = 'SKP External has been cancelled';
          }
          const navigationRef = cancelNavigationRef.current;
          const idToRefresh = cancelId;
          dispatch(
            showAlert({
              show: true,
              title: title,
              description: desc,
              isConfirm: false,
              onCallback: () => {
                // Refresh detail screen by replacing with same route to trigger useEffect
                if (navigationRef) {
                  setSavedFilter({ reset: true });
                  navigationRef.replace('SkpExternalDetailScreen', {
                    id: idToRefresh,
                    refresh: Date.now(),
                    afterCancel: true,
                  });
                }
              },
            }),
          );
          dispatch(showPopup({ name: 'isLoading', value: false }));
          setShowCancelModal(false);
          setCancelId(null);
          setCancelStatus(null);
          cancelNavigationRef.current = null;
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Failed to change status',
            type: 'error',
          });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'A system error occurred',
        type: 'error',
      });
    }
  };

  const handleDeleteSubmit = async (reason) => {
    try {
      if (deleteId) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {
          reason_deleted: reason,
          deleted_reason: reason,
          deleted_by: {
            id: userData?._id || userData?.id,
            name: userData?.name || '',
          },
          deleted_date: new Date().toISOString(),
        };
        const removeSkp = await MsOrder.deleteSkpExternal(deleteId, payload);
        if (removeSkp && removeSkp.data && removeSkp.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          setShowDeleteModal(false);
          setDeleteId(null);
          dispatch(
            showAlert({
              show: true,
              title: 'Delete SKP External',
              description: 'SKP External has been successfully deleted',
              isConfirm: false,
              onCallback: () => {
                if (deleteNavigationRef.current) {
                  setSavedFilter({ reset: true });

                  deleteNavigationRef.current.reset({
                    index: 0,
                    routes: [{ name: 'SkpExternalScreen' }],
                  });
                }
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Failed to delete SKP external',
            type: 'error',
          });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'A system error occurred',
        type: 'error',
      });
    }
  };

  return (
    <>
      <Stack.Navigator
        initialRouteName="SkpExternalScreen"
        screenOptions={(props) => ({
          headerLeft: () => <HamburgerButton {...props} />,
          headerRight: () => <NotificationSettingButton {...props} />,
          ...setDefaultStackOption(insets, CardStyleInterpolators, true),
        })}>
        <Stack.Screen
          name="SkpExternalScreen"
          component={SkpExternalScreen}
          options={getSkpExternalScreenOptions(dispatch, insets)}
        />
        <Stack.Screen
          name="SkpExternalFormScreen"
          component={SkpExternalFormScreen}
          options={getSkpExternalFormScreenOptions()}
        />
        <Stack.Screen
          name="SkpExternalDetailScreen"
          component={SkpExternalDetailScreen}
          options={getSkpExternalDetailScreenOptions(
            dispatch,
            tempData,
            setCancelId,
            setCancelStatus,
            setShowCancelModal,
            setDeleteId,
            setShowDeleteModal,
            cancelNavigationRef,
            deleteNavigationRef,
          )}
        />
        <Stack.Screen
          name="SkpExternalBudgetTableScreen"
          component={SkpExternalBudgetTableScreen}
          options={getSkpExternalBudgetTableScreenOptions()}
        />
        <Stack.Screen
          name="SkpExternalListScreen"
          component={SkpExternalListScreen}
          options={getSkpExternalListScreenOptions()}
        />
      </Stack.Navigator>
      <ModalCancel
        show={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelId(null);
          setCancelStatus(null);
          cancelNavigationRef.current = null;
        }}
        onSubmit={handleCancelSubmit}
        title="Cancel SKP External"
        description="This SKP External will be cancelled."
      />
      <ModalCancel
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteId(null);
          deleteNavigationRef.current = null;
        }}
        onSubmit={handleDeleteSubmit}
        title="Delete SKP External"
        description="This SKP External will be deleted."
      />
    </>
  );
};

const styles = StyleSheet.create({
  headerRightRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default SkpExternalNavigator;
